import { useCallback } from "react";
import { useSpreadsheetRefContextMaybe } from "@components/tasks/SpreadsheetRefContext";
import {
    SpreadsheetReference,
    isSameSpreadsheetViewConfig,
} from "@common/schemas/spreadsheetReference.schema";
import { resolveEffectiveReportCycle } from "@common/helpers/reports";

/**
 * Highlight and view-restore actions for a spreadsheet reference in the
 * mounted task body. A legacy reference (no viewConfig) always matches; a
 * saved view that differs from the live one can only restore that view.
 */
export function useSpreadsheetRefFocus(reference: SpreadsheetReference) {
    const refCtx = useSpreadsheetRefContextMaybe();

    const isLegacy = reference.viewConfig == null;

    // Normalize so a chip saved under By Subclass still matches the live By-Class view.
    const savedViewConfig = reference.viewConfig
        ? {
              ...reference.viewConfig,
              cycle: resolveEffectiveReportCycle({
                  cycle: reference.viewConfig.cycle,
              }),
          }
        : reference.viewConfig;

    const isViewMismatch =
        !isLegacy &&
        refCtx != null &&
        !isSameSpreadsheetViewConfig(
            savedViewConfig,
            refCtx.currentViewConfig ?? undefined,
        );

    const canHighlight = refCtx != null && !isViewMismatch;

    const highlight = useCallback(() => {
        refCtx?.focusSpreadsheetReferences([reference]);
    }, [refCtx, reference]);

    const focusSpreadsheetReferences = refCtx?.focusSpreadsheetReferences;
    const clearHighlight = useCallback(() => {
        focusSpreadsheetReferences?.([]);
    }, [focusSpreadsheetReferences]);

    const applyView = useCallback(() => {
        refCtx?.requestApplyViewConfig(reference);
    }, [refCtx, reference]);

    return {
        isViewMismatch,
        canHighlight,
        highlight,
        clearHighlight,
        applyView,
    };
}
